/**
 * The player docked under the call archive: one recording at a time, heard on this page.
 *
 * Four things about it are deliberate, and each is the answer to a way this goes wrong.
 *
 * - **The link is asked for when play is pressed, never when the list loads.** It lasts five
 *   minutes, and each one handed out is a line in that recording's trail; fifty links for a
 *   page of fifty calls would be fifty listens nobody made.
 * - **An expired link is replaced, and playback carries on from where it was.** A player left
 *   paused over lunch holds a dead link. Pressing play asks for a fresh one first; and should
 *   the store refuse a range request mid-play, the error is caught, a new link fetched and the
 *   position restored once — rather than a button that silently does nothing.
 * - **The audio is never fetched by script.** An `<audio src>` needs no CORS from the store,
 *   and the store answers range requests, so seeking works on a link the page never touches.
 *   A download is the same link signed to arrive as a file, followed by an ordinary anchor.
 * - **Who has listened is on screen.** The trail is the firm's answer to "who heard this", and
 *   showing it beside the player is what makes listening something people know is seen.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { callsApi, type RecordingAccess } from '../calls/api';
import { describe } from '../settings/errors';
import { useTenantFormats } from '../shell/formats';
import { Button, ConfirmDialog, Icon, Pill, Sub, type Tone } from '../ui';
import {
  clock,
  daysLeft,
  isFresh,
  nextSpeed,
  otherParty,
  type ArchivedCall,
  type HeldLink,
} from './callArchive';

/** A listen is ordinary; a copy leaving the firm is worth noticing; a deletion is final. */
const TRAIL_TONES: Record<string, Tone> = {
  Played: 'neutral',
  Downloaded: 'info',
  Deleted: 'danger',
  Expired: 'neutral',
};

export function RecordingPlayer({
  call,
  start,
  previous,
  next,
  onStep,
  onChanged,
  onClose,
}: {
  /** The call in the player. Its recording may have gone; the trail still shows. */
  call: ArchivedCall;
  /**
   * Bumped by the page each time play is pressed on a row, so pressing play on the call
   * already loaded plays it rather than doing nothing because nothing changed.
   */
  start: number;
  previous: ArchivedCall | null;
  next: ArchivedCall | null;
  /** Move the player to another call and play it. */
  onStep: (call: ArchivedCall) => void;
  /** A recording was deleted: the archive should be read again. */
  onChanged: (message: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const formats = useTenantFormats();

  const audio = useRef<HTMLAudioElement>(null);

  // Refs beside the state, because the audio element's events and the retry read them
  // between renders: a closure over the state would hold the link of the render before.
  const link = useRef<HeldLink | null>(null);
  const resumeAt = useRef<number | null>(null);
  const retried = useRef(false);
  const handledStart = useRef(0);

  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [length, setLength] = useState<number | null>(null);
  const [rate, setRate] = useState(1);
  const [fetching, setFetching] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [trail, setTrail] = useState<RecordingAccess[] | null>(null);
  const [showTrail, setShowTrail] = useState(false);
  const [confirming, setConfirming] = useState<'one' | 'customer' | null>(null);

  // Read once when the player opens, as the missed-call list does: a count of days does not
  // need a clock under it, and reading one during render is not a pure render.
  const [openedAt] = useState(() => Date.now());

  const callId = call.id;
  const playable = call.hasRecording;

  const readTrail = useCallback(() => {
    void callsApi
      .trail(callId)
      .then(setTrail)
      .catch(() => setTrail([]));
  }, [callId]);

  /** A fresh link into the player, keeping the position. Every call here is audited. */
  const load = useCallback(async () => {
    const element = audio.current;

    if (element === null) return;

    setFetching(true);

    try {
      const answer = await callsApi.recording(callId);

      // Wherever the old link had got to, the new one starts from — the src changing puts
      // the element back to nought, and `loadedmetadata` moves it on again.
      if (element.currentTime > 0) resumeAt.current = element.currentTime;

      link.current = {
        url: answer.url,
        fetchedAt: Date.now(),
        expiresInSeconds: answer.expiresInSeconds,
      };

      element.src = answer.url;
      element.playbackRate = rate;
      readTrail();
    } finally {
      setFetching(false);
    }
  }, [callId, rate, readTrail]);

  const play = useCallback(async () => {
    const element = audio.current;

    if (element === null || !playable) return;

    setProblem(null);

    try {
      if (!isFresh(link.current, Date.now())) await load();

      await element.play();
    } catch (error) {
      // An AbortError is a play() interrupted by the next src or a pause — somebody pressing
      // the next recording quickly — and is not worth a sentence on the screen.
      if (error instanceof DOMException && error.name === 'AbortError') return;

      setProblem(describe(error, t, 'reports.calls.player.couldNotPlay'));
    }
  }, [load, playable, t]);

  const toggle = useCallback(() => {
    const element = audio.current;

    if (element === null) return;

    if (element.paused) void play();
    else element.pause();
  }, [play]);

  const seekBy = useCallback((seconds: number) => {
    const element = audio.current;

    if (element === null || !element.src) return;

    const end = Number.isFinite(element.duration) ? element.duration : Infinity;

    element.currentTime = Math.max(0, Math.min(end, element.currentTime + seconds));
  }, []);

  // A different call: everything about the last one goes, including its link — a link is
  // for one recording, and the trail must not show the previous call's listeners.
  useEffect(() => {
    const element = audio.current;

    link.current = null;
    resumeAt.current = null;
    retried.current = false;

    if (element !== null) {
      element.pause();
      element.removeAttribute('src');
      element.load();
    }

    // Resetting what the player shows is the point of this effect: it is synchronising the
    // screen with the call that was just handed in.
    // eslint-disable-next-line react/set-state-in-effect
    setPosition(0);
    setLength(null);
    setProblem(null);
    setTrail(null);
    readTrail();
  }, [callId, readTrail]);

  // Play pressed on a row. Guarded by the counter it was pressed with, so a render that
  // repeats the effect — React's development mode does — asks for one link, not two.
  useEffect(() => {
    if (start === 0 || handledStart.current === start) return;

    handledStart.current = start;
    void play();
  }, [start, play]);

  // Space, the arrows, and shift with an arrow for the next recording — but never while
  // somebody is typing, or while focus is on a control that already answers those keys.
  useEffect(() => {
    const busy =
      'input, textarea, select, button, [role="radio"], [role="switch"], [contenteditable="true"]';

    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;

      if (
        target?.closest(busy) ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey
      ) {
        return;
      }

      if (event.key === ' ') {
        event.preventDefault();
        toggle();
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();

        const forward = event.key === 'ArrowRight';

        if (event.shiftKey) {
          const other = forward ? next : previous;
          if (other !== null) onStep(other);
        } else {
          seekBy(forward ? 10 : -10);
        }
      }
    };

    window.addEventListener('keydown', onKey);

    return () => window.removeEventListener('keydown', onKey);
  }, [toggle, seekBy, next, previous, onStep]);

  const download = async () => {
    setProblem(null);

    try {
      const answer = await callsApi.recording(callId, true);

      // An ordinary link to a file the store sends as an attachment: the browser saves it
      // and the page stays where it is. Never fetched here, which would need CORS.
      const anchor = document.createElement('a');

      anchor.href = answer.url;
      anchor.rel = 'noopener';
      document.body.append(anchor);
      anchor.click();
      anchor.remove();

      readTrail();
    } catch (error) {
      setProblem(describe(error, t, 'reports.calls.player.couldNotSave'));
    }
  };

  const remove = async (scope: 'one' | 'customer') => {
    setConfirming(null);
    setProblem(null);

    try {
      audio.current?.pause();

      if (scope === 'customer' && call.customerId) {
        const { deleted } = await callsApi.forgetCustomer(call.customerId);

        onChanged(t('reports.calls.player.deletedCustomer', { count: deleted }));
      } else {
        await callsApi.deleteRecording(callId);

        onChanged(t('reports.calls.player.deletedOne'));
      }

      readTrail();
    } catch (error) {
      setProblem(describe(error, t, 'reports.calls.player.couldNotDelete'));
    }
  };

  const plays = trail?.filter((access) => access.kind === 'Played') ?? [];
  const left = daysLeft(call.recordingExpiresAt, openedAt);
  const number = otherParty(call);
  const known = length ?? call.recordingSeconds ?? 0;

  return (
    <section
      aria-label={t('reports.calls.player.label')}
      className="sticky bottom-0 z-10 mt-auto -mx-8 -mb-6 border-t border-rule bg-surface shadow-lift px-8 py-3"
    >
      <audio
        ref={audio}
        preload="none"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onPlaying={() => {
          retried.current = false;
        }}
        onTimeUpdate={(event) => setPosition(event.currentTarget.currentTime)}
        onLoadedMetadata={(event) => {
          const element = event.currentTarget;

          if (Number.isFinite(element.duration)) setLength(element.duration);

          if (resumeAt.current !== null) {
            element.currentTime = resumeAt.current;
            resumeAt.current = null;
          }
        }}
        onError={(event) => {
          const element = event.currentTarget;

          // No src is not a failure: it is the player between calls.
          if (!element.getAttribute('src') || link.current === null) return;

          // The store refused — almost always the link running out mid-recording. Once,
          // with a fresh link from where it stopped; a second refusal is a real problem.
          if (!retried.current) {
            retried.current = true;
            link.current = null;
            resumeAt.current = position;
            void play();
            return;
          }

          setProblem(t('reports.calls.player.couldNotPlay'));
        }}
      />

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1">
          <Button
            icon
            variant="quiet"
            disabled={previous === null}
            title={t('reports.calls.player.previous')}
            aria-label={t('reports.calls.player.previous')}
            onClick={() => previous && onStep(previous)}
          >
            <Icon name="skip-back" />
          </Button>

          <Button
            icon
            variant="primary"
            disabled={!playable || fetching}
            title={playing ? t('reports.calls.player.pause') : t('reports.calls.player.play')}
            aria-label={playing ? t('reports.calls.player.pause') : t('reports.calls.player.play')}
            onClick={toggle}
          >
            <Icon name={playing ? 'pause' : 'play'} />
          </Button>

          <Button
            icon
            variant="quiet"
            disabled={next === null}
            title={t('reports.calls.player.next')}
            aria-label={t('reports.calls.player.next')}
            onClick={() => next && onStep(next)}
          >
            <Icon name="skip-forward" />
          </Button>
        </div>

        <div className="flex items-center gap-1">
          <Button
            icon
            size="sm"
            variant="quiet"
            disabled={!playable || length === null}
            title={t('reports.calls.player.back10')}
            aria-label={t('reports.calls.player.back10')}
            onClick={() => seekBy(-10)}
          >
            <Icon name="rewind" small />
          </Button>
          <Button
            icon
            size="sm"
            variant="quiet"
            disabled={!playable || length === null}
            title={t('reports.calls.player.forward10')}
            aria-label={t('reports.calls.player.forward10')}
            onClick={() => seekBy(10)}
          >
            <Icon name="fast-forward" small />
          </Button>
        </div>

        <span className="text-secondary text-ink-2 tabular-nums w-[44px] text-right">
          {clock(position)}
        </span>

        <input
          type="range"
          min={0}
          max={known || 1}
          step={0.1}
          value={Math.min(position, known || 1)}
          disabled={!playable || length === null}
          aria-label={t('reports.calls.player.position')}
          className="flex-1 min-w-[160px] accent-brand cursor-pointer disabled:cursor-not-allowed"
          onChange={(event) => {
            const element = audio.current;
            const to = Number(event.target.value);

            setPosition(to);
            if (element) element.currentTime = to;
          }}
        />

        <span className="text-secondary text-ink-3 tabular-nums w-[44px]">{clock(known)}</span>

        <Button
          size="sm"
          variant="quiet"
          className="tabular-nums w-[48px]"
          title={t('reports.calls.player.speed')}
          aria-label={t('reports.calls.player.speed')}
          disabled={!playable}
          onClick={() => {
            const faster = nextSpeed(rate);

            setRate(faster);
            if (audio.current) audio.current.playbackRate = faster;
          }}
        >
          {`${rate}×`}
        </Button>

        <Button size="sm" disabled={!playable} onClick={() => void download()}>
          <Icon name="download" small />
          {t('reports.calls.player.download')}
        </Button>

        <Button
          size="sm"
          variant="danger"
          disabled={!playable}
          onClick={() => setConfirming('one')}
        >
          <Icon name="trash" small />
          {t('reports.calls.player.delete')}
        </Button>

        {call.customerId && (
          <Button size="sm" variant="quiet" onClick={() => setConfirming('customer')}>
            {t('reports.calls.player.deleteCustomer')}
          </Button>
        )}

        <Button
          icon
          size="sm"
          variant="quiet"
          title={t('reports.calls.player.close')}
          aria-label={t('reports.calls.player.close')}
          onClick={() => {
            audio.current?.pause();
            onClose();
          }}
        >
          <Icon name="close" small />
        </Button>
      </div>

      <div className="flex items-center gap-x-3 gap-y-1 flex-wrap mt-2 text-secondary">
        <span className="font-semibold text-ink">
          {call.customerName ?? number ?? t('reports.calls.withheld')}
        </span>

        {call.customerName && number && <span className="text-ink-2">{number}</span>}

        <span className="text-ink-3">{formats.moment(call.startedAt)}</span>

        {call.answeredByName && (
          <span className="text-ink-3">
            {t('reports.calls.player.takenBy', { name: call.answeredByName })}
          </span>
        )}

        {call.orderReference && (
          <Pill tone="brand" soft>
            {t('reports.calls.order', { reference: call.orderReference })}
          </Pill>
        )}

        {!playable && <Pill soft>{t('reports.calls.player.gone')}</Pill>}

        {playable && left !== null && (
          <Sub title={formats.moment(call.recordingExpiresAt!)}>
            {t('reports.calls.player.deletesIn', { count: left })}
          </Sub>
        )}

        <span className="flex-1" />

        <button
          type="button"
          className="text-ink-3 hover:text-ink underline-offset-2 hover:underline"
          aria-expanded={showTrail}
          onClick={() => setShowTrail((shown) => !shown)}
        >
          {trail === null
            ? t('reports.loading')
            : plays.length === 0
              ? t('reports.calls.player.neverPlayed')
              : t('reports.calls.player.playedBy', {
                  count: plays.length,
                  name: plays[0].name ?? t('reports.calls.player.someone'),
                  at: formats.moment(plays[0].at),
                })}
        </button>

        <Sub className="hidden lg:inline">{t('reports.calls.player.keys')}</Sub>
      </div>

      {problem !== null && <p className="m-0 mt-2 text-secondary text-danger">{problem}</p>}

      {showTrail && trail !== null && (
        <ol className="m-0 mt-2 p-0 list-none flex flex-col gap-1 max-h-[160px] overflow-y-auto">
          {trail.length === 0 && (
            <li className="text-ink-3">{t('reports.calls.player.noTrail')}</li>
          )}

          {trail.map((access) => (
            <li key={`${access.kind}-${access.at}`} className="flex items-center gap-3">
              <span className="text-ink-3 tabular-nums w-[150px]">
                {formats.moment(access.at)}
              </span>
              <Pill soft tone={TRAIL_TONES[access.kind] ?? 'neutral'}>
                {t(`reports.calls.trail.${access.kind}`)}
              </Pill>
              <span className="text-ink-2">
                {access.name ??
                  (access.userId
                    ? t('reports.calls.player.someone')
                    : t('reports.calls.trail.retention'))}
              </span>
            </li>
          ))}
        </ol>
      )}

      <ConfirmDialog
        open={confirming === 'one'}
        title={t('reports.calls.player.deleteTitle')}
        confirm={t('reports.calls.player.delete')}
        cancel={t('reports.calls.player.keep')}
        onConfirm={() => void remove('one')}
        onClose={() => setConfirming(null)}
      >
        {t('reports.calls.player.deleteBody', {
          who: call.customerName ?? number ?? t('reports.calls.withheld'),
          at: formats.moment(call.startedAt),
        })}
      </ConfirmDialog>

      <ConfirmDialog
        open={confirming === 'customer'}
        title={t('reports.calls.player.deleteCustomerTitle')}
        confirm={t('reports.calls.player.deleteCustomerConfirm')}
        cancel={t('reports.calls.player.keep')}
        onConfirm={() => void remove('customer')}
        onClose={() => setConfirming(null)}
      >
        {t('reports.calls.player.deleteCustomerBody', {
          who: call.customerName ?? number ?? t('reports.calls.withheld'),
        })}
      </ConfirmDialog>
    </section>
  );
}
